<template>
	<o-section v-bind="{ title, subtitle }">
		<o-alert
			v-if="setup.route.name === 'OrganizationTagDetails'"
			color="warning"
			class="mb-sm">
			{{ $t('common.shared.components.selectUserList.tagWarning') }}
		</o-alert>
		<o-input
			v-if="!hideSearch"
			v-model="setup.search"
			:label="$t('common.shared.components.selectUserList.searchPlaceholder')"
			suffix-font-icon="icon-interface-search"
			:donetyping="600"
			@input="setup.refreshListAsync(true)"/>

		<div v-if="!hideQuickFilters && quickFiltersType">
			<o-chips
				v-for="(chips, index) in setup.quickFiltersOptions"
				:key="index"
				outline
				:color="setup.filterIsSelected(chips) ? 'info' : 'default'"
				@click="setup.selectFilter(chips)">
				{{ chips.label }}
			</o-chips>
		</div>

		<div class="flex mb-sm">
			<o-toggle
				v-if="!!setup.self.suiteUserId"
				v-model="setup.filter.withHiddenUsers"
				:label="$t('common.shared.components.selectUserList.showHiddenUsers')"
				@input="setup.refreshListAsync(true)"/>
		</div>

		<o-list
			v-model:page="setup.page"
			:use-pagination-top="false"
			:list="setup.list"
			:total="setup.total"
			layout="row">
			<template #default="{ item: user }">
				<o-sticker
					v-tooltip="user.isDisabled ? $t('common.shared.components.selectUserList.userDisabled') : undefined"
					:muted="setup.userIsHidden(user)"
					:selected="setup.userIsSelected(user)"
					@click="setup.toggleSelectUser(user)">
					<div class="flex ai-c g-sm">
						<o-avatar
							class="shared-select-user-list__user-avatar"
							size="sm"
							root-url="/app/avatar/"
							:name="user.user.firstName"
							:avatar="user.user.avatarId"/>
						<div>
							<div class="flex ai-c g-xs">
								<strong v-html="user.user.name.mark(setup.search)"/>
								<o-icon
									v-if="setup.userIsHidden(user)"
									v-tooltip="setup.getHiddenUserTooltip(user)"
									font-icon="icon-interface-help-question-circle"/>
							</div>

							<div
								class="text--info"
								v-html="user.user.authLogin.mark(setup.search)"/>
						</div>
						<o-label
							v-if="user.user.id === setup.self.id"
							class="ml-a"
							color="info"
							size="md">
							{{ $t('common.shared.components.selectUserList.you') }}
						</o-label>
					</div>
				</o-sticker>
			</template>
		</o-list>
	</o-section>

	<teleport :to="_modal?.slotFooter">
		<o-section align="stretch">
			<o-button
				outline
				color="danger"
				@click="_modal?.close()">
				{{ $t('common.global.cancel') }}
			</o-button>
			<o-button
				:color="setup.userSelected.length > 0 ? 'brand' : 'default'"
				:disabled="!setup.userSelected.length"
				@click="setup.addUsers()">
				{{ transferCreator ? $t('common.shared.components.selectUserList.setAsCreator') : $t('shared.components.selectUserList.addUsers', { count: setup.userSelected.length }) }}
			</o-button>
		</o-section>
	</teleport>
</template>

<script setup lang="ts">
import { inject } from 'vue';
const _modal = inject<OrionModal>('_modal');
// #region Setup
import type { SharedSelectUserListEmits, SharedSelectUserListProps } from './SharedSelectUserListSetup';
import SharedSelectUserListSetup from './SharedSelectUserListSetup';
const emits = defineEmits<SharedSelectUserListEmits>() as SharedSelectUserListEmits;
const props = withDefaults(defineProps<SharedSelectUserListProps>(), SharedSelectUserListSetup.defaultProps);
const setup = new SharedSelectUserListSetup(props, emits, _modal);
// #endregion
</script>

<style lang="less">
.shared-select-user-list {
	position: relative;
	height: 100%;
	padding-bottom: 3rem;
	// background: fade(red, 10);

	&__search {
		margin-bottom: 1rem;
	}

	&__filter-section {
		display: flex;
		gap: var(--space-sm);
		align-items: center;
		margin-bottom: 1rem;
	}

	&__filter {
		cursor: pointer;

		&:hover {
			opacity: 0.7;
		}
	}

	&__user {
		cursor: pointer;
		display: flex;
		gap: 1rem;
		align-items: center;
		padding: 0.625rem 1rem;

		&:first-of-type {
			border-top-left-radius: 5px;
			border-top-right-radius: 5px;
		}

		&:last-of-type {
			border-bottom-right-radius: 5px;
			border-bottom-left-radius: 5px;
		}

		p {
			margin: unset;
		}

		&:hover:not(.shared-select-user-list__user--selected) {
			background: var(--grey-lighter);
		}

		&--selected {
			background: rgba(var(--rgb-info), 0.15);
		}
	}

	&__user-avatar {
		width: 2.5rem;
		height: 2.5rem;
	}

	&__actions {
		position: absolute;
		bottom: 0;
		width: 100%;
	}

	&__user-name {
		font-weight: 700;
		color: var(--grey-darker);
	}

	&__user-mail {
		color: var(--info);
		text-decoration: underline;
	}

	.arm-list {
		overflow: scroll;
		max-height: 25rem;
	}

	.arm-paginate {
		margin: 1.25rem auto 0.625rem;
	}

	.arm-footer-fixed {
		display: none;
	}

	&__user-self {
		position: absolute;
		right: 25px;
	}
}
</style>
